import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import {
  Sun, PlugZap, Zap, ArrowRight, ArrowLeft, Check, ChevronDown, ChevronUp,
  Sparkles, Home as HomeIcon, Loader2, ShieldCheck, Info, ExternalLink, SkipForward,
} from "lucide-react";

const STEPS = ["welcome", "method", "manual", "cloud", "done"];

export default function Onboarding() {
  const { user, loading: userLoading } = useApp();
  const nav = useNavigate();
  const [step, setStep] = useState("welcome");
  const [method, setMethod] = useState(null); // "manual" | "cloud"
  const [createdPlantId, setCreatedPlantId] = useState(null);

  if (!userLoading && !user) return <Navigate to="/login" replace />;

  const go = (s) => setStep(s);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F9F8F6] to-[#EAE7E0] grain-bg pb-16">
      <div className="max-w-3xl mx-auto px-6 pt-10">
        {/* Progress bar */}
        <ProgressBar step={step} />

        <AnimatePresence mode="wait">
          {step === "welcome" && (
            <StepWrap key="welcome">
              <WelcomeStep user={user} onNext={() => go("method")} onSkip={() => nav("/dashboard")} />
            </StepWrap>
          )}
          {step === "method" && (
            <StepWrap key="method">
              <MethodStep
                onPick={(m) => { setMethod(m); go(m); }}
                onBack={() => go("welcome")}
                onSkip={() => nav("/dashboard")}
              />
            </StepWrap>
          )}
          {step === "manual" && (
            <StepWrap key="manual">
              <ManualStep
                onBack={() => go("method")}
                onDone={(plantId) => { setCreatedPlantId(plantId); setMethod("manual"); go("done"); }}
              />
            </StepWrap>
          )}
          {step === "cloud" && (
            <StepWrap key="cloud">
              <CloudStep
                onBack={() => go("method")}
                onDone={(plantId) => { setCreatedPlantId(plantId); setMethod("cloud"); go("done"); }}
              />
            </StepWrap>
          )}
          {step === "done" && (
            <StepWrap key="done">
              <DoneStep method={method} plantId={createdPlantId} onGoDashboard={() => nav("/dashboard")} onGoCoach={() => nav("/coach")} />
            </StepWrap>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ------------------------------- LAYOUT ------------------------------- */

const StepWrap = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.35 }}
    className="mt-6"
  >{children}</motion.div>
);

const ProgressBar = ({ step }) => {
  const idx = STEPS.indexOf(step);
  const pct = ((idx + 1) / STEPS.length) * 100;
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest font-black text-[#6E7A71]">Configurazione impianto</div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#6E7A71]">Step {idx + 1} di {STEPS.length}</div>
      </div>
      <div className="h-1.5 rounded-full bg-[#DDE0D8] overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.5 }}
          className="h-full bg-[#EAB308]"
        />
      </div>
    </div>
  );
};

/* -------------------------- STEP: WELCOME -------------------------- */

function WelcomeStep({ user, onNext, onSkip }) {
  return (
    <div className="card-surface p-10 text-center">
      <div className="w-20 h-20 rounded-full bg-[#EAB308] flex items-center justify-center mx-auto mb-6">
        <Sparkles size={38} className="text-[#17301C]" />
      </div>
      <div className="label-eco mb-3">Benvenuto{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</div>
      <h1 className="text-4xl font-black text-[#17301C] tracking-tight leading-tight">
        Configuriamo il tuo <span className="italic font-light text-[#EAB308]">impianto</span>
      </h1>
      <p className="text-base text-[#3A4A3E] mt-4 max-w-lg mx-auto">
        In 2 minuti avrai una dashboard con produzione, risparmio e consigli AI su misura per il tuo impianto fotovoltaico.
      </p>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 text-left max-w-2xl mx-auto">
        <MiniPoint icon={Zap} text="Monitoraggio in tempo reale (con inverter compatibile)" />
        <MiniPoint icon={Sun} text="Produzione stimata per la tua città e orientamento" />
        <MiniPoint icon={ShieldCheck} text="Credenziali cifrate con AES-128 (Fernet)" />
      </div>
      <div className="mt-10 flex items-center justify-center gap-3">
        <button data-testid="onboard-skip" onClick={onSkip} className="px-5 py-3 rounded-full text-sm font-semibold text-[#6E7A71] hover:bg-white/60">
          Salta per ora
        </button>
        <button data-testid="onboard-start" onClick={onNext} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#17301C] text-white font-black text-sm hover:bg-[#0f2013]">
          Iniziamo <ArrowRight size={15} />
        </button>
      </div>
    </div>
  );
}

const MiniPoint = ({ icon: Icon, text }) => (
  <div className="flex items-start gap-2 text-sm text-[#3A4A3E]">
    <div className="w-7 h-7 rounded-lg bg-[#EAB308]/15 flex items-center justify-center flex-shrink-0">
      <Icon size={14} className="text-[#8B7000]" />
    </div>
    <div className="leading-tight">{text}</div>
  </div>
);

/* -------------------------- STEP: METHOD -------------------------- */

function MethodStep({ onPick, onBack, onSkip }) {
  return (
    <div className="card-surface p-8 sm:p-10">
      <h2 className="text-3xl font-black text-[#17301C] mb-2">Come vuoi configurare l&apos;impianto?</h2>
      <p className="text-[#6E7A71] mb-8">Scegli il metodo più comodo. Puoi sempre cambiare in seguito.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MethodCard
          testid="method-manual"
          icon={HomeIcon}
          badge="⚡ Il più veloce"
          badgeColor="#8BA892"
          title="Aggiungi manualmente"
          duration="30 secondi"
          desc="Serve solo: nome, potenza in kWp, città. Ideale se non conosci le credenziali dell'inverter."
          bullets={["Solo 3 campi obbligatori", "Stime accurate per la tua città", "Nessuna credenziale richiesta"]}
          onClick={() => onPick("manual")}
        />
        <MethodCard
          testid="method-cloud"
          icon={PlugZap}
          badge="🎯 Massima precisione"
          badgeColor="#EAB308"
          title="Collega inverter cloud"
          duration="2 minuti"
          desc="Il tuo impianto in tempo reale: produzione, autoconsumo, batteria, storico completo."
          bullets={["Dati LIVE ogni 5 minuti", "SolarEdge, Growatt, Huawei, Fronius…", "Credenziali cifrate AES"]}
          onClick={() => onPick("cloud")}
        />
      </div>

      <div className="mt-8 flex items-center justify-between">
        <button data-testid="onboard-back" onClick={onBack} className="inline-flex items-center gap-2 text-sm text-[#6E7A71] hover:text-[#17301C]">
          <ArrowLeft size={14} /> Indietro
        </button>
        <button data-testid="onboard-skip-method" onClick={onSkip} className="inline-flex items-center gap-2 text-sm text-[#6E7A71] hover:text-[#17301C]">
          Salta <SkipForward size={14} />
        </button>
      </div>
    </div>
  );
}

const MethodCard = ({ testid, icon: Icon, badge, badgeColor, title, duration, desc, bullets, onClick }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="text-left card-surface p-6 hover:border-[#17301C] hover:shadow-lg transition-all group cursor-pointer border-2 border-transparent"
  >
    <div className="flex items-start justify-between mb-4">
      <div className="w-12 h-12 rounded-xl bg-[#17301C] flex items-center justify-center group-hover:bg-[#EAB308] transition-colors">
        <Icon size={22} className="text-[#EAB308] group-hover:text-[#17301C] transition-colors" />
      </div>
      <div className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider text-white" style={{ background: badgeColor }}>
        {badge}
      </div>
    </div>
    <div className="text-xl font-black text-[#17301C]">{title}</div>
    <div className="text-xs font-bold uppercase tracking-widest text-[#6E7A71] mt-1">{duration}</div>
    <p className="text-sm text-[#3A4A3E] mt-3 leading-relaxed">{desc}</p>
    <ul className="mt-4 space-y-1.5">
      {bullets.map((b, i) => (
        <li key={i} className="text-xs text-[#3A4A3E] flex items-start gap-2">
          <Check size={12} className="text-[#8BA892] mt-0.5 flex-shrink-0" /> {b}
        </li>
      ))}
    </ul>
    <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-black text-[#17301C] group-hover:text-[#EAB308]">
      Continua <ArrowRight size={14} />
    </div>
  </button>
);

/* -------------------------- STEP: MANUAL -------------------------- */

function ManualStep({ onBack, onDone }) {
  const [form, setForm] = useState({
    name: "",
    kwp: 6,
    location: "Roma",
    battery_kwh: 0,
    monthly_kwh_consumption: 350,
    orientation: "south",
    energy_price_eur_kwh: 0.28,
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.location.trim() || form.kwp <= 0) {
      toast.error("Compila nome, potenza kWp e città");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        kwp: parseFloat(form.kwp),
        location: form.location.trim(),
        installed_date: new Date().toISOString().slice(0, 10),
        panels: Math.max(2, Math.round(parseFloat(form.kwp) / 0.45)), // auto
        battery_kwh: parseFloat(form.battery_kwh) || 0,
        monthly_kwh_consumption: parseFloat(form.monthly_kwh_consumption) || 0,
        orientation: form.orientation,
        energy_price_eur_kwh: parseFloat(form.energy_price_eur_kwh) || 0.28,
      };
      const { data } = await api.post("/plants", payload);
      toast.success("Impianto creato!");
      onDone(data?.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel salvataggio. Riprova.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-surface p-8">
      <div className="flex items-center gap-2 mb-6">
        <div className="w-10 h-10 rounded-lg bg-[#8BA892]/20 flex items-center justify-center">
          <HomeIcon size={18} className="text-[#166534]" />
        </div>
        <div>
          <h2 className="text-2xl font-black text-[#17301C]">Dati impianto</h2>
          <p className="text-sm text-[#6E7A71]">Solo 3 campi obbligatori</p>
        </div>
      </div>

      <div className="space-y-4">
        <Field label="Nome impianto" hint="Come vuoi chiamarlo (es. Casa Roma)" required>
          <input data-testid="onb-plant-name" required value={form.name} onChange={(e) => update("name", e.target.value)}
            placeholder="Es. Casa Roma" className="onb-inp" />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Potenza (kWp)" hint="Trovi il valore sulla scheda tecnica dell'inverter" required>
            <input data-testid="onb-plant-kwp" type="number" step="0.1" min="0.5" required
              value={form.kwp} onChange={(e) => update("kwp", e.target.value)} className="onb-inp" />
          </Field>
          <Field label="Città o Comune" hint="Serve per calcolare irraggiamento" required>
            <input data-testid="onb-plant-location" required value={form.location}
              onChange={(e) => update("location", e.target.value)}
              placeholder="Es. Rocca di Neto" className="onb-inp" />
          </Field>
        </div>

        {/* Advanced (collapsed) */}
        <button
          type="button"
          data-testid="onb-toggle-advanced"
          onClick={() => setShowAdvanced((s) => !s)}
          className="mt-2 flex items-center gap-2 text-sm font-bold text-[#17301C] hover:text-[#EAB308]"
        >
          {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Dettagli avanzati (opzionale)
        </button>
        <AnimatePresence>
          {showAdvanced && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-4 overflow-hidden"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <Field label="Batteria (kWh)" hint="0 se non ne hai">
                  <input data-testid="onb-plant-battery" type="number" step="0.1" min="0"
                    value={form.battery_kwh} onChange={(e) => update("battery_kwh", e.target.value)} className="onb-inp" />
                </Field>
                <Field label="Consumo mensile stimato (kWh)">
                  <input data-testid="onb-plant-consumption" type="number" min="0"
                    value={form.monthly_kwh_consumption} onChange={(e) => update("monthly_kwh_consumption", e.target.value)} className="onb-inp" />
                </Field>
                <Field label="Orientamento">
                  <select data-testid="onb-plant-orientation" value={form.orientation} onChange={(e) => update("orientation", e.target.value)} className="onb-inp">
                    <option value="south">Sud</option>
                    <option value="se">Sud-Est</option>
                    <option value="sw">Sud-Ovest</option>
                    <option value="east">Est</option>
                    <option value="west">Ovest</option>
                    <option value="north">Nord</option>
                  </select>
                </Field>
                <Field label="Prezzo energia (€/kWh)">
                  <input data-testid="onb-plant-price" type="number" step="0.01" min="0.01"
                    value={form.energy_price_eur_kwh} onChange={(e) => update("energy_price_eur_kwh", e.target.value)} className="onb-inp" />
                </Field>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <button type="button" data-testid="onboard-back-manual" onClick={onBack}
          className="inline-flex items-center gap-2 text-sm text-[#6E7A71] hover:text-[#17301C]">
          <ArrowLeft size={14} /> Indietro
        </button>
        <button type="submit" disabled={saving} data-testid="onb-save-plant"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#EAB308] text-[#17301C] font-black text-sm hover:bg-[#f5bc1b] disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          {saving ? "Salvataggio..." : "Crea impianto"}
        </button>
      </div>

      <style>{`.onb-inp{width:100%;padding:0.65rem 0.85rem;border-radius:0.55rem;border:1.5px solid #DDE0D8;background:white;font-size:0.925rem;transition:border-color .18s;} .onb-inp:focus{outline:none;border-color:#17301C;box-shadow:0 0 0 2px rgba(23,48,28,.12);}`}</style>
    </form>
  );
}

const Field = ({ label, hint, required, children }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <label className="text-[11px] uppercase tracking-widest font-black text-[#17301C]">
        {label} {required && <span className="text-[#EAB308]">*</span>}
      </label>
    </div>
    {children}
    {hint && <div className="mt-1 text-[11px] text-[#6E7A71] flex items-start gap-1.5"><Info size={10} className="mt-0.5 flex-shrink-0" /> {hint}</div>}
  </div>
);

// Brand-colored initial avatar (replaces Clearbit external logos which fail unreliably)
const BRAND_COLORS = {
  S: "#E31E24", G: "#2B7A0B", H: "#C7000B", F: "#0066B3", E: "#FF6C0C",
  M: "#005A9C", Z: "#0071BC", A: "#6E7A71",
};
function BrandInitial({ label, size = "md" }) {
  const initial = (label || "?").trim().charAt(0).toUpperCase();
  const bg = BRAND_COLORS[initial] || "#17301C";
  const dims = size === "lg" ? "w-11 h-11 text-lg" : "w-8 h-8 text-sm";
  return (
    <div className={`${dims} rounded-lg flex items-center justify-center font-black text-white shadow-sm flex-shrink-0`} style={{ background: bg }}>
      {initial}
    </div>
  );
}

/* -------------------------- STEP: CLOUD -------------------------- */

function CloudStep({ onBack, onDone }) {
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [phase, setPhase] = useState("plant"); // "plant" → "brand" → "credentials"
  const [plantForm, setPlantForm] = useState({ name: "", kwp: 6, location: "Roma" });
  const [plantId, setPlantId] = useState(null);
  const [credentials, setCredentials] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/inverter-brands").then(({ data }) => setBrands(data?.brands || [])).catch(() => setBrands([]));
  }, []);

  const currentBrand = brands.find((b) => b.key === selectedBrand);

  const createPlantAndContinue = async (e) => {
    e.preventDefault();
    if (!plantForm.name.trim() || !plantForm.location.trim() || parseFloat(plantForm.kwp) <= 0) {
      toast.error("Compila nome, potenza e città");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: plantForm.name.trim(),
        kwp: parseFloat(plantForm.kwp),
        location: plantForm.location.trim(),
        installed_date: new Date().toISOString().slice(0, 10),
        panels: Math.max(2, Math.round(parseFloat(plantForm.kwp) / 0.45)),
      };
      const { data } = await api.post("/plants", payload);
      setPlantId(data?.id);
      setPhase("brand");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore creazione impianto");
    } finally {
      setSaving(false);
    }
  };

  const pickBrand = (key) => {
    setSelectedBrand(key);
    setCredentials({});
    setTestResult(null);
    setPhase("credentials");
  };

  const testConn = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/plants/${plantId}/connect/test`, {
        brand: selectedBrand,
        credentials,
      });
      setTestResult(data);
      if (data.ok) toast.success(data.message || "Test riuscito!");
      else toast.error(data.error || "Test fallito");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore test connessione");
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/plants/${plantId}/connect`, { brand: selectedBrand, credentials });
      toast.success("Inverter collegato!");
      onDone(plantId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  /* Phase 1: create the plant */
  if (phase === "plant") {
    return (
      <form onSubmit={createPlantAndContinue} className="card-surface p-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-lg bg-[#EAB308]/20 flex items-center justify-center">
            <PlugZap size={18} className="text-[#8B7000]" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-[#17301C]">Dati impianto (rapidi)</h2>
            <p className="text-sm text-[#6E7A71]">Poi selezioniamo la marca dell&apos;inverter</p>
          </div>
        </div>

        <div className="space-y-4">
          <Field label="Nome impianto" required>
            <input data-testid="onb-cloud-name" required value={plantForm.name}
              onChange={(e) => setPlantForm({ ...plantForm, name: e.target.value })}
              placeholder="Es. Casa Roma" className="onb-inp" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Potenza (kWp)" required>
              <input data-testid="onb-cloud-kwp" type="number" step="0.1" min="0.5" required
                value={plantForm.kwp} onChange={(e) => setPlantForm({ ...plantForm, kwp: e.target.value })} className="onb-inp" />
            </Field>
            <Field label="Città / Comune" required>
              <input data-testid="onb-cloud-location" required value={plantForm.location}
                onChange={(e) => setPlantForm({ ...plantForm, location: e.target.value })}
                placeholder="Es. Rocca di Neto" className="onb-inp" />
            </Field>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <button type="button" onClick={onBack} data-testid="onboard-back-cloud"
            className="inline-flex items-center gap-2 text-sm text-[#6E7A71] hover:text-[#17301C]">
            <ArrowLeft size={14} /> Indietro
          </button>
          <button type="submit" disabled={saving} data-testid="onb-cloud-next"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#17301C] text-white font-black text-sm hover:bg-[#0f2013] disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
            {saving ? "Attendi..." : "Continua"}
          </button>
        </div>

        <style>{`.onb-inp{width:100%;padding:0.65rem 0.85rem;border-radius:0.55rem;border:1.5px solid #DDE0D8;background:white;font-size:0.925rem;} .onb-inp:focus{outline:none;border-color:#17301C;box-shadow:0 0 0 2px rgba(23,48,28,.12);}`}</style>
      </form>
    );
  }

  /* Phase 2: select brand */
  if (phase === "brand") {
    return (
      <div className="card-surface p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-black text-[#17301C]">Quale marca ha il tuo inverter?</h2>
          <p className="text-sm text-[#6E7A71]">Seleziona la marca. Se non la conosci, scegli &quot;Altro / Non lo so&quot;.</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {brands.map((b) => (
            <button
              key={b.key}
              onClick={() => pickBrand(b.key)}
              data-testid={`onb-brand-${b.key}`}
              className="relative card-surface p-4 hover:border-[#17301C] hover:shadow-md transition-all text-left border-2 border-transparent flex flex-col items-start gap-2 min-h-[100px]"
            >
              <BrandInitial label={b.label} />
              <div className="font-bold text-sm text-[#17301C] leading-tight">{b.label}</div>
              {b.supported ? (
                <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-[#8BA892] text-white">Live</span>
              ) : (
                <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-[#EAE7E0] text-[#6E7A71]">48h</span>
              )}
            </button>
          ))}
        </div>

        <div className="mt-6 p-3 rounded-lg bg-[#EAB308]/10 border border-[#EAB308]/30 text-xs text-[#3A4A3E] flex items-start gap-2">
          <Info size={13} className="text-[#8B7000] mt-0.5 flex-shrink-0" />
          <div>
            <b>Live</b> = monitoraggio real-time attivo subito. <b>48h</b> = credenziali cifrate, attivazione manuale entro 48h dal nostro tecnico.
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button onClick={() => setPhase("plant")} data-testid="onboard-back-brand"
            className="inline-flex items-center gap-2 text-sm text-[#6E7A71] hover:text-[#17301C]">
            <ArrowLeft size={14} /> Indietro
          </button>
          <button onClick={() => onDone(plantId)} data-testid="onboard-skip-brand"
            className="inline-flex items-center gap-2 text-sm text-[#6E7A71] hover:text-[#17301C]">
            Salta questo passo <SkipForward size={14} />
          </button>
        </div>
      </div>
    );
  }

  /* Phase 3: credentials */
  return (
    <div className="card-surface p-8">
      <div className="flex items-center gap-3 mb-5">
        <BrandInitial label={currentBrand?.label || "?"} size="lg" />
        <div>
          <h2 className="text-2xl font-black text-[#17301C]">Collega {currentBrand?.label}</h2>
          {currentBrand?.portal_url && (
            <a href={currentBrand.portal_url} target="_blank" rel="noreferrer noopener"
              className="text-xs text-[#3A4A3E] inline-flex items-center gap-1 hover:text-[#17301C]">
              Portale ufficiale <ExternalLink size={10} />
            </a>
          )}
        </div>
      </div>

      {currentBrand?.instructions?.length > 0 && (
        <div className="mb-5 p-4 rounded-xl bg-[#F9F8F6] border border-[#DDE0D8]">
          <div className="text-[10px] uppercase tracking-widest font-black text-[#6E7A71] mb-2">Come trovare le credenziali</div>
          <ol className="space-y-1.5">
            {currentBrand.instructions.map((s, i) => (
              <li key={i} className="text-sm text-[#3A4A3E] flex items-start gap-2">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#17301C] text-white text-[10px] font-black flex items-center justify-center">{i + 1}</span>
                {s}
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="space-y-4">
        {currentBrand?.fields?.map((f) => (
          <Field key={f.key} label={f.label} hint={f.help} required={currentBrand.required?.includes(f.key)}>
            <input
              type={f.type}
              value={credentials[f.key] || ""}
              onChange={(e) => setCredentials({ ...credentials, [f.key]: e.target.value })}
              data-testid={`onb-cred-${f.key}`}
              placeholder={f.label}
              autoComplete="off"
              className="onb-inp"
            />
          </Field>
        ))}
      </div>

      {testResult && (
        <div className={`mt-4 p-3 rounded-lg text-sm ${testResult.ok ? "bg-[#8BA892]/15 border border-[#8BA892] text-[#17301C]" : "bg-[#991B1B]/10 border border-[#991B1B] text-[#991B1B]"}`}>
          {testResult.ok ? (
            <div className="flex items-start gap-2">
              <Check size={14} className="mt-0.5 text-[#166534]" />
              <div>
                <b>Test riuscito</b>
                {testResult.site_name && <div className="text-xs mt-0.5">Impianto: {testResult.site_name}</div>}
                {testResult.peak_power_kwp > 0 && <div className="text-xs">Potenza: {testResult.peak_power_kwp} kWp</div>}
                {testResult.coming_soon && <div className="text-xs mt-1">{testResult.message}</div>}
              </div>
            </div>
          ) : (
            <><b>❌ {testResult.error}</b><div className="text-xs mt-1">Ricontrolla le credenziali e riprova.</div></>
          )}
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 gap-3">
        <button
          onClick={testConn}
          disabled={testing || saving || !currentBrand?.required?.every((k) => (credentials[k] || "").trim())}
          data-testid="onb-cred-test"
          className="px-4 py-2.5 rounded-lg border-2 border-[#17301C] text-[#17301C] font-bold text-sm hover:bg-[#17301C] hover:text-white transition-colors disabled:opacity-40"
        >
          {testing ? <Loader2 size={14} className="animate-spin inline" /> : "Test connessione"}
        </button>
        <button
          onClick={save}
          disabled={saving || testing || !currentBrand?.required?.every((k) => (credentials[k] || "").trim())}
          data-testid="onb-cred-save"
          className="px-4 py-2.5 rounded-lg bg-[#EAB308] text-[#17301C] font-black text-sm hover:bg-[#f5bc1b] transition-colors disabled:opacity-40"
        >
          {saving ? <Loader2 size={14} className="animate-spin inline" /> : "Collega inverter"}
        </button>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <button onClick={() => setPhase("brand")} data-testid="onboard-back-cred"
          className="inline-flex items-center gap-2 text-sm text-[#6E7A71] hover:text-[#17301C]">
          <ArrowLeft size={14} /> Cambia marca
        </button>
        <button onClick={() => onDone(plantId)} data-testid="onboard-skip-cred"
          className="inline-flex items-center gap-2 text-sm text-[#6E7A71] hover:text-[#17301C]">
          Salta per ora <SkipForward size={14} />
        </button>
      </div>

      <style>{`.onb-inp{width:100%;padding:0.65rem 0.85rem;border-radius:0.55rem;border:1.5px solid #DDE0D8;background:white;font-size:0.925rem;} .onb-inp:focus{outline:none;border-color:#17301C;box-shadow:0 0 0 2px rgba(23,48,28,.12);}`}</style>
    </div>
  );
}

/* -------------------------- STEP: DONE -------------------------- */

function DoneStep({ method, plantId, onGoDashboard, onGoCoach }) {
  return (
    <div className="card-surface p-10 text-center">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", damping: 12 }}
        className="w-20 h-20 rounded-full bg-[#8BA892] flex items-center justify-center mx-auto mb-6"
      >
        <Check size={40} className="text-white" strokeWidth={3} />
      </motion.div>
      <h1 className="text-4xl font-black text-[#17301C] tracking-tight">Tutto pronto! 🎉</h1>
      <p className="text-[#3A4A3E] mt-3 max-w-md mx-auto">
        {method === "cloud"
          ? "Il tuo impianto è collegato. I dati arriveranno entro pochi minuti."
          : "Il tuo impianto è configurato. Puoi collegare l’inverter reale in qualsiasi momento dalla Dashboard."}
      </p>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg mx-auto">
        <button onClick={onGoDashboard} data-testid="onb-done-dashboard"
          className="p-5 rounded-xl bg-[#17301C] text-white hover:bg-[#0f2013] transition-colors text-left">
          <div className="flex items-center gap-2 mb-1">
            <Zap size={16} className="text-[#EAB308]" />
            <div className="font-black">Vai alla Dashboard</div>
          </div>
          <div className="text-xs text-white/70">Guarda produzione, guadagni e stato impianto</div>
        </button>
        <button onClick={onGoCoach} data-testid="onb-done-coach"
          className="p-5 rounded-xl bg-[#EAB308] text-[#17301C] hover:bg-[#f5bc1b] transition-colors text-left">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={16} />
            <div className="font-black">Chiedi al Coach AI</div>
          </div>
          <div className="text-xs text-[#17301C]/70">Consigli in tempo reale sul tuo impianto</div>
        </button>
      </div>
    </div>
  );
}
